import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import Contents from "./Contents";
import Tags from  "../public/Tags";
import Search from "../public/Search";
import { isLoggedIn, LogOut } from '../login/Login';
import { ContentIF, TagIF } from '../types';
import { TrashIconContainer, TrashIcon, TagIconContainer, TagIcon, MenuIconContainer, MenuIcon, InfoIconContainer, InfoIcon, SunIcon } from '../icons';
import { deleteTags } from '../api/tag';
import axios from 'axios';
import {HEADER_BOTTOM, HEADER_HEIGHT} from '../public/Header';
import { useTheme } from '../theme/ThemeProvider';
import ThemeToggle from '../public/ThemeToggle';
import Copyright from '../public/Copyright';

export const ONE_PAGE_MAX = 12;

/* ---------------- 애니메이션 ---------------- */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ---------------- 전체 배경 ---------------- */

const BG = styled.div`
  height: 100vh;                 /* 🔥 전체 화면 고정 */
  display: flex;
  flex-direction: column;
  overflow: hidden;              /* 🔥 전체 스크롤 막기 */
  background: linear-gradient(135deg, 
    ${props => props.theme.ROOT_BACKGROUND.FIRST} 0%, 
    ${props => props.theme.ROOT_BACKGROUND.LAST} 100%);
  font-family: 'Pretendard', -apple-system, sans-serif;
`;

/* ---------------- Header ---------------- */

export const Head = styled.div`
  background: ${props => props.theme.HEADER.BACKGROUND};
  backdrop-filter: blur(12px);

  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 0 2rem;
  width: 100%;
  height: ${HEADER_HEIGHT}px;

  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;

  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  padding: 0 4rem;
  box-sizing: border-box; // padding이 너비에 포함되게끔
`;

const BlogName = styled.h1`
  font-size: 1.5rem;
  font-weight: 800;
  color: ${props => props.theme.HEADER.BLOG_NAME};
  letter-spacing: -1px;
  cursor: pointer;
`;

/* ---------------- Body ---------------- */

const Body = styled.div`
  display: flex;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  gap: 2rem;

  /* 핵심: 헤더를 제외한 정확한 높이 설정 */
  height: calc(100vh - ${HEADER_HEIGHT}px); 
  margin-top: ${HEADER_HEIGHT}px;
  
  /* 자식들이 각자 스크롤을 가지므로 부모는 넘치는 것을 숨김 */
  overflow: hidden; 
  flex: 1;

  padding-bottom: 3rem; /* 하단 여백 추가 */
`;

/* ---------------- BOX 공통 ---------------- */

const Box = styled.div`
  background: ${props => props.theme.BOX_BACKGROUND};
  border-radius: 1rem;
  padding: 1.2rem;
  box-shadow: 0 4px 6px -1px ${props => props.theme.BOX_SHADOW.FIRST},
              0 2px 4px -1px ${props => props.theme.BOX_SHADOW.LAST};
  border: 1px solid ${props => props.theme.BOX_BORDER};
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

/* ---------------- Sidebar ---------------- */

const SideBar = styled.div`
  width: 250px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  /* 핵심: 부모(Body) 높이 안에서 스크롤 발생 */
  height: 100%; 
  overflow-y: auto; 
  padding: 1.5rem 0; /* 상하 여백만 적절히 추가 */
  
  min-height: 0;
  animation: ${fadeIn} 0.6s ease-out;

  &::-webkit-scrollbar {
    display: none;
  }
`;
const Menu = styled(Box)``;
const Taglist = styled(Box)``;
const Info = styled(Box)``;

const SubTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${props => props.theme.MENU.SUBTITLE_COLOR};
  text-transform: uppercase;
  margin-bottom: 1rem;

  display: flex;
  align-items: center;
  justify-content: space-between;

  & > div {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const MenuList = styled.ul`
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const MenuItem = styled.li`
  list-style: none;
  padding: 0.6rem 0.8rem;
  font-size: 0.95rem;
  color: ${props => props.theme.MENU.ITEM_COLOR};
  border-radius: 0.6rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.MENU.HOVER.BACKGROUND};
    color: ${props => props.theme.MENU.HOVER.FONT_COLOR};
    padding-left: 1.2rem;
  }
`;

const InfoList = styled(MenuList)``;

const InfoItem = styled.li`
  list-style: none;
  padding: 0.6rem 0.8rem;
  font-size: 0.95rem;
  color: ${props => props.theme.MENU.ITEM_COLOR};
`;

const InfoCnt = styled.strong`
  color: ${props => props.theme.HEADER.BLOG_NAME};
  font-weight: 600;
`;

/* ---------------- Content 영역 ---------------- */

const ContentList = styled.div`
  flex: 1;
  height: 100%; /* 부모 높이 전체 사용 */
  overflow-y: auto; 

  /* Header만큼 또 띄울 필요가 없으므로 padding 조절 */
  padding: 1.5rem 1rem 5rem 0; 

  &::-webkit-scrollbar {
    display: none;
  }
`;
/* ---------------- 아이콘 ---------------- */

const StyledTrash = styled(TrashIconContainer)`
  cursor: pointer;
  color: #94a3b8;
  transition: color 0.2s;

  &:hover {
    color: #ef4444;
  }
`;

/* ---------------- Page ---------------- */

const Pages = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 1rem;
`;

const PageBtn = styled.div<{$isCur:boolean}>`
  width: 2rem;
  height: 2rem;
  border: 1px solid ${props => props.theme.PAGE_BTN.BORDER};
  border-radius: 7px;
  background-color: ${({$isCur}) => ($isCur ? props => props.theme.PAGE_BTN.BACKGROUND.CURRENT : props => props.theme.PAGE_BTN.BACKGROUND.NOT_CURRENT)};
  box-shadow: 3px 2px 2px ${props => props.theme.BOX_SHADOW.FIRST};
  color: ${props => props.theme.PAGE_BTN.FONT_COLOR};

  font-weight: 600;
  
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: ${props => props.theme.PAGE_BTN.HOVER.BACKGROUND};
    color: ${props => props.theme.PAGE_BTN.HOVER.FONT_COLOR};
  }
`;

const DotContainer = styled.div`
  display: flex;
  gap: 0.3rem;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const Dot = styled.span`
  width: 2px;
  height: 2px;
  background-color: ${props => props.theme.PAGE_BTN.FONT_COLOR};
  border-radius: 50%;
`;

/* ---------------- Home Component ---------------- */

function Home() {
  const [tags, setTags] = useState<TagIF[]>([]);
  const [selectedTags, setSelectedTags] = useState<TagIF[]>([]);
  const [searchedTerm, setSearchedTerm] = useState<string>("");
  const [totalContents, setTotalContents] = useState<number>(0);
  const [totalViews, setTotalViews] = useState<number>(0);
  const [contents, setContents] = useState<ContentIF[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleLength, setVisibleLength] = useState<number>(0);
  const [pages, setPages] = useState<number[]>([]);
  const [curPage, setCurPage] = useState<number>(1);

  const navigate = useNavigate();
  const { theme, toggle } = useTheme();

  const getTags = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/tags/getTags`);
      setTags(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getTags();
    axios.get(`${process.env.REACT_APP_API_URL}/contents/getContents`)
      .then(res => {
        setContents(res.data);
        // 여기서 통계 바로 계산 (자식한테 시키지 않음)
        setTotalContents(res.data.length);
        const views = res.data.reduce((acc:number, cur:ContentIF) => acc + (cur.view || 0), 0);
        setTotalViews(views);
        setLoading(false);
      });
  }, []); // 딱 한 번만 실행

  // 콘텐츠 페이지 버튼 
  useEffect(() => {
    const temp = [];
    for (let i=1; i<visibleLength/ONE_PAGE_MAX + 1; i++) {
      temp.push(i);
    }
    setPages(temp);

  }, [visibleLength]);

  useEffect(() => {
    setCurPage(1); // 검색어나 태그가 바뀌면 페이지 초기화
  }, [searchedTerm, selectedTags]);

  const variableMenu = () => {
    if (isLoggedIn()) {
      return (
        <>
          <MenuItem onClick={() => navigate("/writing")}>Writing</MenuItem>
          <MenuItem onClick={() => { LogOut(); navigate('/') }}>Log Out</MenuItem>
        </>
      );
    }
    return <MenuItem onClick={() => navigate('/login')}>Log In</MenuItem>;
  };

  const handleDeleteTags = async () => {
    try {
      await deleteTags(selectedTags);
      setContents(contents.map(content => ({
        ...content,
        tags: content.tags.filter(tag => selectedTags.every(selTag => selTag.id !== tag.id))
      })));
      setSelectedTags([]);
      getTags();
    } catch (error) {
      console.error("태그 삭제 실패:", error);
    }
  };

  return (
    <BG>
          <Head>
          <BlogName onClick={() => navigate("/")}>Lamiro</BlogName>
          <Search
            searchedTerm={searchedTerm}
            onSearchedTerm={setSearchedTerm}
            styleFor={process.env.REACT_APP_STYLE_HOME_SEARCH}
            placeholder="🔍"
          />
          <ThemeToggle toggle={toggle} mode={theme} />
        </Head>
        <Body>
          <SideBar>
            <Info>
              <SubTitle>
                <InfoIconContainer>
                  <InfoIcon />
                  Info
                </InfoIconContainer>
              </SubTitle>
              <InfoList>
                <InfoItem>
                  <span>Total Contents: </span>
                  <InfoCnt>{totalContents}</InfoCnt>
                </InfoItem>
                <InfoItem>
                  <span>Total View: </span>
                  <InfoCnt>{totalViews}</InfoCnt>
                </InfoItem>
              </InfoList>
            </Info>
            <Menu>
              <SubTitle>
                <MenuIconContainer>
                  <MenuIcon />
                  Menu
                </MenuIconContainer>
              </SubTitle>

              <MenuList>
                <MenuItem onClick={() => navigate("/aboutme")}>About Me</MenuItem>
                {variableMenu()}
              </MenuList>
            </Menu>

            <Taglist>
              <SubTitle>
                <TagIconContainer>
                  <TagIcon />
                  Tags
                </TagIconContainer>

                {isLoggedIn() && (
                  <StyledTrash onClick={handleDeleteTags}>
                    <TrashIcon />
                  </StyledTrash>
                )}
              </SubTitle>

              <TagContainer>
                <Tags
                  tags={tags}
                  selectedTags={selectedTags}
                  onSelectedTags={setSelectedTags}
                />
              </TagContainer>
            </Taglist>
          </SideBar>

          <ContentList id="scroll__container">
            {/*Contents에 데이터를 직접 넘김*/}
            <Contents
              contents={contents}
              loading={loading}      
              selectedTags={selectedTags}
              searchedTerm={searchedTerm}
              curPage={curPage}
              onVisibleLength={setVisibleLength}
            />
            <Pages>
                {pages.map((page, idx)=> {
                  const isFirstOrLast = idx === 0 || idx === pages.length - 1;
                  const isAroundCur = Math.abs(page - curPage) <= 1;
                  const isSecondAroundCur = Math.abs(page - curPage) === 2;
                  const isCur = page === curPage;

                  if(isFirstOrLast || isAroundCur) {
                    return (
                      <PageBtn
                        key={page}
                        $isCur={isCur}
                        onClick={() => setCurPage(page)}
                      >
                        {page}
                      </PageBtn>
                    );
                  }

                  if(isSecondAroundCur) {
                    return <DotContainer>
                      <Dot />
                      <Dot />
                      <Dot />
                    </DotContainer>
                  }

                  return null;
                })}
            </Pages>
            <Copyright />
          </ContentList>
        </Body>
    </BG>
  );
}

export default Home;